import React from "react";
import { createPortal } from "react-dom";
import { Box, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DirectionsIcon from "@mui/icons-material/Directions";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { COLORS } from "./constants";

const StationPopup = ({ station, container, onClose, allStations }) => {
  const navigate = useNavigate();
  if (!container || !station) return null;

  const googleMapsUrl = station.lat && station.lon
    ? `https://www.google.com/maps/dir/?api=1&destination=${station.lat},${station.lon}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(station.location)}`;

  return createPortal(
    <Box 
      sx={{ 
        width: { xs: 190, sm: 260 }, 
        bgcolor: "#fff", 
        borderRadius: "14px", 
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
      }}
    >
      {/* IMAGE HEADER  */}
      <Box sx={{ position: "relative", height: { xs: 70, sm: 100 }, bgcolor: "#eee" }}>
        <Box
          component="img"
          src={station.thumbnail || station.mainImage || "/default-station.png"}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={(e) => e.target.style.display = 'none'}
        />
        <Box 
            sx={{ 
                position: 'absolute', inset: 0, 
                background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)' 
            }} 
        />
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          size="small"
          sx={{
            position: "absolute", top: 4, right: 4,
            bgcolor: "rgba(0,0,0,0.4)", color: "#fff",
            "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
            width: 22, height: 22
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        
        {/* Title overlay */}
        <Box sx={{ position: 'absolute', bottom: 6, left: 10, right: 10 }}>
             <Typography 
               variant="caption" 
               sx={{ 
                 color: '#fff',
                 fontWeight: 700,
                 bgcolor: COLORS.primary, // Changed to solid color for better readability
                 px: 0.8, py: 0.2,
                 borderRadius: '4px',
                 display: 'inline-block',
                 mb: 0.3,
                 fontSize: { xs: '0.65rem', sm: '0.75rem' }
               }}
             >
                {station.distance?.toFixed(1)} KM
             </Typography>
             <Typography 
               noWrap
               sx={{ 
                 color: '#fff', 
                 fontWeight: 700, 
                 fontSize: { xs: '0.8rem', sm: '0.95rem' }, 
                 textShadow: '0 2px 4px rgba(0,0,0,0.5)', 
                 lineHeight: 1.2, 
               }}
             >
                {station.location}
             </Typography>
        </Box>
      </Box>

      {/* ACTIONS */}
      <Box sx={{ p: { xs: 1, sm: 1.5 }, display: 'flex', gap: 0.8 }}>
        <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<DirectionsIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />}
            onClick={() => window.open(googleMapsUrl, "_blank")}
            sx={{ 
                borderRadius: '8px', 
                borderColor: '#ddd', 
                color: COLORS.text, 
                textTransform: 'none', 
                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                py: { xs: 0.4, sm: 0.8 },
                minWidth: 0 // Prevents flex issues
            }}
        >
            Route
        </Button>
        <Button
            fullWidth
            variant="contained"
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />}
            onClick={() => navigate("/stations/station_details", { state: { currentStation: station, station, allStations } })}
            sx={{ 
                borderRadius: '8px', 
                bgcolor: COLORS.primary, 
                boxShadow: 'none', 
                textTransform: 'none', 
                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                py: { xs: 0.4, sm: 0.8 },
                minWidth: 0
            }}
        >
            View
        </Button>
      </Box>
    </Box>,
    container
  );
};

export default StationPopup;